import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModelViewer from './pages/ModelViewer';
import ModelScreenshot from './pages/ModelScreenshot';
import AdminPage from './pages/AdminPage';
import MintPage from './pages/MintPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { WalletProvider, useWallet } from './contexts/WalletContext';
import { ThemeProvider } from './contexts/ThemeProvider';

function App() {
  return (
    <ThemeProvider>
      <WalletProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Routes>
                <Route path="/model-viewer" element={<ModelViewer />} />
                <Route path="/screenshot/:fileId" element={<ModelScreenshot />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/mint" element={<MintPage />} />
                <Route path="/" element={<HomePage />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </Router>
      </WalletProvider>
    </ThemeProvider>
  );
}

function HomePage() {
  const { t } = useTranslation();
  const { isConnected, connectWallet } = useWallet();
  const navigate = useNavigate();
  
  // Обработчик нажатия на кнопку "Загрузить модель"
  const handleUploadClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Если пользователь не авторизован, пытаемся подключить кошелек
    if (!isConnected) {
      const connected = await connectWallet();
      
      // Если подключение успешно, переходим на страницу загрузки
      if (connected) {
        navigate('/model-viewer');
      }
    } else {
      // Если пользователь уже авторизован, просто переходим на страницу загрузки
      navigate('/model-viewer');
    }
  };
  
  return (
    <div className="bg-black text-white">
      {/* Hero секция */}
      <div className="relative overflow-hidden py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight font-heading mb-6">
              <span className="block">PEOPLE</span>
              <span className="block text-blue-500">FIGHT FOR MONEY</span>
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto font-body">
              {t('hero.description')}
            </p>
            
            {/* GIF-анимация */}
            <div className="mt-10 flex justify-center">
              <img 
                src={require('./components/gifs/frontendGif.gif')} 
                alt="3D модель в действии" 
                className="rounded-lg shadow-xl max-w-full h-auto"
                style={{ maxHeight: '400px' }}
              />
            </div>
            
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/model-viewer"
                onClick={handleUploadClick}
                className="px-8 py-4 bg-blue-600 text-white font-body font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('hero.uploadModel')}
              </a>
              <Link
                to="/model-viewer"
                className="px-8 py-4 bg-gray-800 text-white font-body font-medium rounded-md hover:bg-gray-700 transition-colors"
              >
                {t('hero.viewModels')}
              </Link>
            </div>
          </div>
        </div>
        
        {/* Декоративные элементы */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-black/80"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <svg className="h-96 w-96 text-blue-500/10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
            </svg>
          </div>
        </div>
      </div>

      {/* Секция с описанием */}
      <div className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold font-heading mb-8">
            {t('features.title')}<br />
            <span className="text-blue-500">{t('features.subtitle')}</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto font-body mb-16">
            {t('features.description')}
          </p>
          
          <div className="inline-block bg-gray-800 px-6 py-2 mb-16">
            <span className="font-body text-lg">{t('features.tagline')}</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="w-12 h-12 bg-blue-600 rounded-md flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium font-heading mb-2">{t('features.uploadModels')}</h3>
              <p className="text-gray-300 font-body">{t('features.uploadDescription')}</p>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="w-12 h-12 bg-blue-600 rounded-md flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium font-heading mb-2">{t('features.createCharacters')}</h3>
              <p className="text-gray-300 font-body">{t('features.createDescription')}</p>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="w-12 h-12 bg-blue-600 rounded-md flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium font-heading mb-2">{t('features.secureStorage')}</h3>
              <p className="text-gray-300 font-body">{t('features.storageDescription')}</p>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="w-12 h-12 bg-blue-600 rounded-md flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium font-heading mb-2">{t('features.pvpBattles')}</h3>
              <p className="text-gray-300 font-body">{t('features.battlesDescription')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA секция */}
      <div className="py-20 bg-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold font-heading mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto font-body mb-10">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/model-viewer"
              onClick={handleUploadClick}
              className="px-8 py-4 bg-blue-600 text-white font-body font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('cta.start')}
            </a>
            <Link
              to="/model-viewer"
              className="px-8 py-4 bg-white text-blue-900 font-body font-medium rounded-md hover:bg-gray-100 transition-colors"
            >
              {t('cta.learnMore')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App; 